<script setup lang="ts">
import { i18n } from '@/i18n';
import { InventoryDashboardViewModel } from '../inventoryDashboardViewModel';
import InventoryDashboardChildLayout from '../InventoryDashboardChildLayout.vue';
import KpiAchievementChart from '@/inventoryDashboardModule/components/chart/KpiAchievementChart.vue';
import { useInventoryDashboardStore } from '../useInventoryDashboardStore';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { DimensionType, DimensionTypes, MetricType, MetricTypes } from '@/inventoryDashboardModule/domains/entities/Dashboard';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { IYearMonth } from '@/coreDomainsModule/date';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';

const t = i18n.t;
const store = useInventoryDashboardStore();
const { state } = storeToRefs(store);
const { getByDimensionData } = store;

const chartComponentRef = ref<InstanceType<typeof KpiAchievementChart> | null>(null);

const beforeValues = ref<{
  metricType: MetricType;
  dimensionType: DimensionType;
  displayYearMonthPeriod: [IYearMonth, IYearMonth];
}>({
  metricType: state.value.information.metricType,
  dimensionType: state.value.information.dimensionType,
  displayYearMonthPeriod: [
    DateService.getShiftedIYearMonthFromNowDateByMonths(-12),
    DateService.getShiftedIYearMonthFromNowDateByMonths(6),
  ],
});

const latestAchievementInfos = computed(() => {
  return state.value.information.dimensionValueGroups.map((dimensionValueGroup) => {
    const latestConfirmed = dimensionValueGroup.amounts.reduce((acc, a) =>
      a.salesResult && DateService.compareYearMonths(a.yearMonth, acc.yearMonth) === 1 ? a : acc
    );
    const targetDate = DateService.convertIYearMonthToLastDateIDate(latestConfirmed.yearMonth);
    const targetKpi = dimensionValueGroup.kpi.find(
      (k) =>
        (!k.valid_from || DateService.compareDates(k.valid_from, targetDate) !== 1) &&
        (!k.valid_to || DateService.compareDates(k.valid_to, targetDate) !== -1)
    );
    return {
      name: dimensionValueGroup.dimensionValues.name || t(UNASSIGNED_COLUMN),
      yearMonth: latestConfirmed.turnover && targetKpi ? DateService.convertIYearMonthToYYYYMMWithSlash(latestConfirmed.yearMonth) : '--',
      value: latestConfirmed.turnover && targetKpi ? `${((targetKpi.kpiValue / latestConfirmed.turnover) * 100).toFixed(1)}% ` : '--',
    };
  });
});

/**
 * カードをクリックしてグラフの表示非表示を切り替えるハンドラ
 * @param index - カードのindex
 */
const onClickCard = (index: number) => {
  chartComponentRef.value?.toggleDataset(index);
};

/**
 * 表示期間を変更した際のハンドラ
 * @param start - 開始年月
 * @param end - 終了年月
 */
const onChangeYearMonthPeriod = async (start?: IYearMonth, end?: IYearMonth) => {
  if (start) {
    state.value.displayYearMonthPeriod[0] = start;
  }
  if (end) {
    state.value.displayYearMonthPeriod[1] = end;
  }
  await getByDimensionData();
};

onMounted(async () => {
  beforeValues.value = {
    metricType: state.value.information.metricType,
    dimensionType: state.value.information.dimensionType,
    displayYearMonthPeriod: state.value.displayYearMonthPeriod,
  };
  state.value.information.metricType = MetricTypes.Turnover;
  state.value.information.dimensionType = DimensionTypes.AggregateBusinessResponsibleUser;
  state.value.displayYearMonthPeriod = [
    DateService.getShiftedIYearMonthFromNowDateByMonths(-12),
    DateService.getShiftedIYearMonthFromNowDateByMonths(6),
  ];
  await waitLoading(getByDimensionData(), undefined, '');
});

onBeforeUnmount(() => {
  state.value.information.metricType = beforeValues.value.metricType;
  state.value.information.dimensionType = beforeValues.value.dimensionType;
  state.value.displayYearMonthPeriod = beforeValues.value.displayYearMonthPeriod;
});
</script>
<template>
  <InventoryDashboardChildLayout>
    <div id="overview-list-detail" class="body">
      <div class="body__header">
        <div class="body__header__desc">
          <div class="body__header__title">
            <div class="body__header__title--text">{{ t('担当者別KPI達成率推移') }}</div>
          </div>
          <div class="body__header__desc">{{ t('各担当者の在庫回転率KPI達成率の月次推移を表示しています') }}</div>
        </div>
        <div class="body__header__ym">
          {{ t('表示期間') }}
          <MonthPicker
            :model-value="state.displayYearMonthPeriod[0]"
            :locale="i18n.locale"
            :prop-format="t('MonthPickerFormat')"
            class="month-picker"
            @update:model-value="(value) => onChangeYearMonthPeriod(value, undefined)"
          />
          <span class="body__header__ym-divide">~</span>
          <MonthPicker
            :model-value="state.displayYearMonthPeriod[1]"
            :locale="i18n.locale"
            :prop-format="t('MonthPickerFormat')"
            class="month-picker"
            @update:model-value="(value) => onChangeYearMonthPeriod(undefined, value)"
          />
        </div>
      </div>
      <div class="body__graph">
        <KpiAchievementChart
          ref="chartComponentRef"
          :dimension-value-groups="state.information.dimensionValueGroups"
          :from-year-month="state.displayYearMonthPeriod[0]"
          :to-year-month="state.displayYearMonthPeriod[1]"
        />
      </div>
    </div>
    <div class="card__container">
      <button v-for="(latestAchievementInfo, index) in latestAchievementInfos" :key="index" class="card" @click="() => onClickCard(index)">
        <Mdi v-if="!chartComponentRef?.isDatasetVisible(index)" icon="visibility_off" class="card__icon" />
        <Mdi v-else icon="visibility" class="card__icon" />
        <div class="card__title">
          {{ latestAchievementInfo.name || t(UNASSIGNED_COLUMN) }}
        </div>
        <div class="card__achievement">{{ latestAchievementInfo.value }}</div>
        <div class="card__date">
          {{ latestAchievementInfo.yearMonth }}
        </div>
      </button>
    </div>
  </InventoryDashboardChildLayout>
</template>
<style lang="scss" scoped>
.body {
  display: grid;
  padding: 8px 16px 24px 16px;
  border-radius: 4px;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  gap: 16px;
  &__header {
    display: grid;
    padding: 8px 0;
    gap: 16px;
    color: $textDefault;
    &__title {
      display: grid;
      height: 24px;
      grid-template-columns: max-content max-content;
      align-items: center;
      gap: 8px;
      &--text {
        font-weight: 700;
        font-size: 16px;
        line-height: 1;
      }
    }
    &__desc {
      font-weight: 400;
      font-size: 14px;
      line-height: 1.3;
    }
    &__ym {
      display: grid;
      grid-template-columns: repeat(4, max-content);
      gap: 8px;
      align-items: center;
      &-divide {
        font-size: 18px;
        font-weight: 700;
      }
    }
  }
}

.card {
  display: grid;
  grid-template-columns: max-content 1.5fr 1fr 1fr;
  align-items: end;
  gap: 4px;
  padding: 16px;
  height: fit-content;
  border-radius: 4px;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  &__container {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
  &__icon {
    font-size: 32px;
  }
  &__title {
    color: $textSecondary;
    font-weight: 700;
    font-size: 22px;
    line-height: 1.3;
  }
  &__achievement {
    color: $textDefault;
    font-weight: 700;
    font-size: 22px;
    line-height: 1.3;
  }
  &__date {
    justify-self: end;
  }
}

a {
  &:hover,
  &:focus,
  &:focus-visible {
    color: inherit !important;
    background-color: inherit !important;
  }
}

.month-picker {
  width: 140px;
  height: 36px;
  background-color: $semanticWhite;
  border-radius: 4px;
  border: 1px solid $borderDefault;
  display: grid;
  place-items: center;
}
</style>

<style lang="scss">
.month-picker input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
